import type { Location, NavigateFunction } from 'react-router-dom';

export type WalletLocationState = {
  background?: Location;
};

export type OpenWalletOptions = {
  /** Prefill top-up amount (usually the shortfall). */
  amount?: number;
  /** Target purchase total — used to show “you need $X more”. */
  need?: number;
  returnTo?: string;
  /**
   * After a successful top-up, automatically complete this tariff purchase
   * (`POST tariffs/orderTariff`) instead of sending the user back to tap Buy again.
   * Value is the public catalog plan uuid (not a provider package code).
   */
  buyPackageCode?: string;
  /**
   * After a successful top-up, automatically pay this pending eSIM order
   * (`PATCH esim-order/:id/pay`).
   */
  buyOrderId?: string;
  /** Promo code to apply when auto-completing a tariff purchase after top-up. */
  buyPromoCode?: string;
  /** Days for a day-pass / unlimited plan (eSIM Access periodNum). */
  buyPeriodNum?: number;
};

/** Stripe USD floor — not a product “always top up $5” rule. */
const MIN_STRIPE_USD = 0.5;

/** Round up to cents so Stripe / charge amounts stay precise. */
export function roundUpUsd(value: number): number {
  return Math.ceil(Math.max(0, value) * 100) / 100;
}

/**
 * How much to top up so balance covers `need`.
 * Uses the real shortfall (e.g. $1.44 plan → top up $1.44), not a $5 floor.
 */
export function topupAmountForNeed(need: number, balance: number): number {
  const shortfall = roundUpUsd(need - balance);
  if (shortfall <= 0) return MIN_STRIPE_USD;
  return Math.max(shortfall, MIN_STRIPE_USD);
}

/** Open wallet as a bottom-sheet modal over the current page (keeps background mounted). */
export function openWallet(
  navigate: NavigateFunction,
  location: Location,
  options?: OpenWalletOptions,
) {
  const params = new URLSearchParams();
  if (options?.amount != null && options.amount > 0) {
    params.set('amount', String(options.amount));
  }
  if (options?.need != null && options.need > 0) {
    params.set('need', String(options.need));
  }
  if (options?.returnTo) {
    params.set('returnTo', options.returnTo);
  }
  if (options?.buyPackageCode) {
    params.set('buyPackage', options.buyPackageCode);
  }
  if (options?.buyOrderId) {
    params.set('buyOrder', options.buyOrderId);
  }
  if (options?.buyPromoCode) {
    params.set('buyPromo', options.buyPromoCode);
  }
  if (options?.buyPeriodNum != null && options.buyPeriodNum >= 1) {
    params.set('buyDays', String(options.buyPeriodNum));
  }
  const search = params.toString();

  navigate(
    {
      pathname: '/topup',
      search: search ? `?${search}` : '',
    },
    {
      state: { background: location } satisfies WalletLocationState,
    },
  );
}

export function getWalletBackground(location: Location): Location | null {
  const state = location.state as WalletLocationState | null;
  return state?.background ?? null;
}
